/* eslint-disable @typescript-eslint/no-explicit-any */
import { useEffect, useState, useRef } from "react";
import { Loader } from "components/loader/loader";
import SVGComponent from "components/svg/svg";
import context from "core/tv-guide.context";
import { SelectOption } from "core/types";
import { RootElementId } from "core/constants";
import SelectValue from "./select-value";

// Data object for list
// {
//   label: '',
//   icon: '',
//   iconLt: '',
//   value: ''
// }

type SelectOptionProps = {
  label?: string;
  selectType: string;
  list: SelectOption[]
  callback: (value: any) => void;
  value?: any;
  analyticTags?: any;
  icons?: boolean;
  isChannel?: boolean;
}

/**
 * Idea taken from:
 * https://css-tricks.com/striking-a-balance-between-native-and-custom-select-elements/
 *
 * TLDR:
 * - Create a custom select (divs) and native select element for custom styling and native support
 * - Swap out custom/native select in CSS based on if platform supports hover. (Mobile doesn't)
 *
 */
export const SelectComponent = (props: SelectOptionProps) => {
  const [currentValue, setCurrentValue] = useState<any>(undefined);
  const [clickedValue, setClickedValue] = useState<any>(undefined);
  const [open, setOpen] = useState(false);
  const baseRef = useRef<any>(undefined);
  const [currentList, setCurrentList] = useState<SelectOption[]>([]);

  const {
    analyticTags = () => { return {} },
    selectType,
    isChannel = false,
    value,
    callback,
    icons = false
  } = props;

  useEffect(() => {
    if (!props.list.length || props.list === currentList) {
      return;
    }

    setCurrentList(props.list);

    if (!currentValue) {
      let item = value ? props.list.find(x => x.value === value) : props.list[0];
      item = item ?? props.list[0];

      setCurrentValue(item);
      setClickedValue(item);
    }
  }, [props.list]);

  useEffect(() => {
    programmaticValueChange(value)
  }, [props.value]);

  useEffect(() => {
    if (clickedValue) {
      setCurrentValue(clickedValue);
    }
  }, [clickedValue]);

  useEffect(() => {
    document.addEventListener("mousedown", outsideClickHandler);
    return () => {
      document.removeEventListener("mousedown", outsideClickHandler);
    };
  }, [baseRef]);

  const programmaticValueChange = (value: any) => {
    const match = currentList?.find((item: any) => item.value === value);

    if (match) {
      setCurrentValue(match);
      return;
    }

    if (isChannel && !currentValue && currentList && currentList.length) {
      setCurrentValue(currentList[0]);
    }
  }

  const toggleOpen = () => {
    const newValue = !open;
    setOpen(newValue);

    document.getElementById(RootElementId)?.classList.toggle("open-list", newValue)
  }

  const outsideClickHandler = (event: any) => {
    // If the target is not a child of this select, close it
    if (baseRef.current && !baseRef.current.contains(event.target)) {
      setOpen(false);
    }
  }

  const selectItemInList = (item: SelectOption) => {
    setClickedValue(item);
    callback(item);
  }

  const selectItemInSelect = (itemLabel: any) => {
    const matchingItem = currentList?.find((item: any) => item.label === itemLabel);

    if (!matchingItem) {
      return;
    }

    selectItemInList(matchingItem);
  }

  if (!currentList?.length) {
    return (
      <div className="select-block">
        <Loader type="progress-bar" />
      </div>
    );
  }

  const listItemWithoutIcons = (item: SelectOption, index: number) => {
    return (
      <div
        className="select-item"
        key={item.label + index}
        {...(analyticTags(`${item.label}`.replace(/[^a-z0-9:]+/gi, "_")))}
        onClick={() => {
          toggleOpen();
          selectItemInList(item);
        }}
      >
        <p>{item.label}</p>
      </div>
    );
  }


  const listItem = (item: SelectOption, index: number) => {
    if (!icons) {
      return listItemWithoutIcons(item, index);
    }

    return (
      <div
        className="select-item icons"
        key={item.label + index}
        {...(analyticTags(`${item.label}`.replace(/[^a-z0-9:]+/gi, "_")))}
        onClick={() => {
          toggleOpen();
          selectItemInList(item);
        }}
      >
        <p>
          <span className="select-icon">
            <img
              alt={item.label}
              src={item.icon !== "" ? item.icon : item.iconLt}
            />
          </span>
          <span className="select-item-label">
            {item.label}
          </span>
        </p>
      </div>
    );
  }

  let selectCssName = `select-block ${selectType}-select`;
  selectCssName += open ? " open" : "";
  selectCssName += context.channelTags && selectType === "channel" ? "specific-channel" : "";

  const dropListCssName = `droplist${icons ? " icons" : ""} open`;

  return (
    <div
      className={selectCssName}
      ref={baseRef}
      onClick={() => {
        toggleOpen();
      }}
    >
      {props.label && <label className="select-label">{props.label}</label>}

      {/* Standard list */}
      <select
        className="standard-select jcf-ignore"
        value={currentValue?.label}
        // selected={currentValue?.label}
        onChange={(ev) => {
          if (ev.target.value !== currentValue) {
            toggleOpen();
            selectItemInSelect(ev.target.value)
          }
        }}>
        {currentList?.map((item: any, index: number) => {
          return (
            <option key={item.value + index} value={item.label}>
              {item.label}
            </option>
          );
        })}
      </select>
      {/* Custom list */}
      <div className="tvg-custom-select"
        {...(analyticTags(currentValue?.label ?? ""))}>
        <SelectValue currentValue={currentValue} showIcons={icons} />

        {open && (
          <div
            className={dropListCssName}
          >
            {currentList.map((item: any, index: number) => listItem(item, index))}
          </div>
        )}
      </div>
      <span className="arrow-holder">
        <SVGComponent type="drop-down-arrow" />
      </span>
    </div>
  );
}

export default SelectComponent;
